<script lang="ts">
  import { fade } from 'svelte/transition';
  import CheckIcon from '$icons/CheckIcon.svelte';
  import { onMount } from 'svelte';
  import { goto } from '$app/navigation';

  import type { MyGalleriesSingleResponse } from '$src/utils/api/gallery.util';
  import Media from '../common/media/Media.svelte';
  import UserAvatarWrapper from '../common/user/userAvatarWrapper.svelte';

  export let variant: 'vertical' | 'horizontal' = 'horizontal';
  export let selectable = false;
  export let selected = false;
  export let containerCls = '';
  export let disableTransitions = false;

  export let gallery: MyGalleriesSingleResponse = null;

  let renderedCollaborators: MyGalleriesSingleResponse['collaborators'] = [];

  //$: renderedCollaborators = gallery?.collaborators.concat([gallery.creator]);

  let displayGalleryCover = '';

  function determineGalleryCover() {
    if (!gallery || (gallery.posts.length < 1 && !gallery.coverImageUrl)) return;

    if (gallery.coverImageUrl) {
      displayGalleryCover = gallery.coverImageUrl;
      return;
    }

    for (let i = 0; i < gallery.posts.length; i++) {
      for (let j = 0; j < gallery.posts[i].media?.length; j++) {
        if (
          gallery.posts[i].media[j].thumbnails.length > 0 &&
          gallery.posts[i].media[j].thumbnails[0]
        ) {
          displayGalleryCover = gallery.posts[i].media[j].thumbnails[0].toString();
          return;
        }

        if (gallery.posts[i].media[j].images.length > 0 && gallery.posts[i].media[j].images[0]) {
          displayGalleryCover = gallery.posts[i].media[j].images[0];
          return;
        }
      }
    }
  }

  onMount(determineGalleryCover);

  function handleClick() {
    if (!gallery) {
      return;
    }

    if (selectable) {
      selected = !selected;
      return;
    }

    goto('/gallery/' + gallery.slug);
  }
</script>

{#if variant === 'horizontal'}
  <!-- svelte-ignore a11y-no-static-element-interactions -->
  <div
    on:click={handleClick}
    on:keydown={handleClick}
    class="{containerCls} relative flex rounded-xl outline outline-1 shadow-md bg-white outline-garden-grey-300 w-full h-48 select-none cursor-pointer"
    in:fade|global={{ duration: disableTransitions ? 0 : 400 }}
  >
    {#if selectable}
      <div
        class="absolute top-2 left-2 rounded-full w-7 h-7 border-4 grid place-items-center {selected
          ? 'border-white bg-brand-green'
          : 'border-grey-neutral-30 border-opacity-80 bg-grey-transparency-dark bg-opacity-60'} "
      >
        {#if selected}
          <CheckIcon class="stroke-white" size="18" />
        {/if}
      </div>
    {/if}

    <div class="bg-gray-100 w-[45%] rounded-l-xl overflow-hidden">
      {#if displayGalleryCover}
        <Media class=" object-cover w-full h-full" src={displayGalleryCover} alt="Gallery Cover" />
      {/if}
    </div>

    <div class="px-4 py-6 flex flex-col gap-2">
      <h3 class="text-grey-transparency-dark font-medium">{gallery?.title}</h3>
      <div class="flex gap-1 text-grey-transparency-dark text-opacity-60 text-sm">
        <span>{gallery?.posts.length} posts</span>
        <span>•</span>
        <span>{gallery?.talents.length} talents</span>
        <span>•</span>
        <div class=" h-full flex items-center relative">
          {#each renderedCollaborators as collaborator, i}
            <div
              class="rounded-full w-6 h-6 bg-white relative"
              style="transform: translateX(-{i * 7}px); z-index: {i * -1 + 10};"
            >
              <div
                class="rounded-full w-5 h-5 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
              >
                <UserAvatarWrapper
                  cls={'w-8 h-8'}
                  profileImage={collaborator?.profileImage}
                  userName={collaborator?.userName}
                />
              </div>
            </div>
          {/each}
        </div>
      </div>
    </div>
  </div>
{:else if variant === 'vertical'}
  <!-- svelte-ignore a11y-no-static-element-interactions -->
  <div
    on:click={handleClick}
    on:keydown={handleClick}
    class="{containerCls} relative flex flex-col bg-white rounded-xl shadow --updated-border-outline w-full h-[392px] select-none cursor-pointer"
    in:fade|global={{ duration: disableTransitions ? 0 : 400 }}
  >
    {#if selectable}
      <div
        class="absolute top-2 left-2 rounded-full w-7 h-7 border-4 grid place-items-center {selected
          ? 'border-white bg-brand-green'
          : 'border-grey-neutral-30 border-opacity-80 bg-grey-transparency-dark bg-opacity-60'} "
      >
        {#if selected}
          <CheckIcon class="stroke-white" size="18" />
        {/if}
      </div>
    {/if}

    <div class="bg-gray-100 w-full rounded-t-xl overflow-hidden h-[70%]">
      {#if displayGalleryCover}
        <Media class="object-cover w-full h-full" src={displayGalleryCover} alt="Gallery Cover" />
      {/if}
    </div>

    <div class="px-4 py-6 flex flex-col gap-2">
      <h3
        class="text-grey-transparency-dark font-medium empty:bg-gray-100 h-6 rounded empty:w-32"
        title={gallery?.title || ''}
      >
        {gallery?.title || ''}
      </h3>
      <div class="flex flex-wrap gap-1 text-grey-transparency-dark text-opacity-60 text-sm">
        <div class="empty:bg-gray-100 h-5 rounded empty:w-8">
          {gallery?.posts?.length || 0}
        </div>

        {gallery?.posts.length === 1 ? 'post' : 'posts'}

        <span>•</span>

        <div class="empty:bg-gray-100 h-5 rounded empty:w-8">
          {gallery?.talents?.length || 0}
        </div>

        {gallery?.talents.length === 1 ? 'talent' : 'talents'}

        {#if renderedCollaborators?.length}
          <span>•</span>
        {/if}

        <div class=" h-full flex items-center relative">
          {#each renderedCollaborators || [] as collaborator, i}
            <div
              class="rounded-full w-6 h-6 bg-white relative"
              style="transform: translateX(-{i * 7}px); z-index: {i * -1 + 10};"
            >
              <div
                class="rounded-full w-5 h-5 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
              >
                <UserAvatarWrapper
                  cls={'w-5 h-5'}
                  profileImage={collaborator?.profileImage}
                  userName={collaborator?.userName}
                />
              </div>
            </div>
          {/each}
        </div>
      </div>
    </div>
  </div>
{/if}
